import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { OnlineService } from '../../utils/online.service';

@Injectable({
    providedIn: 'root'
})
export class VersionCheckService {
    private currentSignature: string | null = null;
    private updateAvailable = new BehaviorSubject<boolean>(false);
    private checkInProgress = false;

    readonly updateAvailable$ = this.updateAvailable.asObservable();
    readonly intervalMs = environment.versionCheckIntervalMs ?? 60 * 60 * 1000;

    constructor(private onlineService: OnlineService) { }

    get isUpdateAvailable(): boolean {
        return this.updateAvailable.value;
    }

    initialize(): void {
        this.captureCurrentVersion();
    }

    checkForUpdate(): void {
        if (this.checkInProgress || this.isUpdateAvailable) {
            return;
        }

        this.onlineService.isOnline().pipe(take(1)).subscribe(isOnline => {
            if (!isOnline) {
                return;
            }

            this.compareCurrentVersion();
        });
    }

    getBundleSignature(indexHtml: string): string {
        const scripts = this.getScriptSources(indexHtml)
            .map(src => this.normalizeScriptSource(src))
            .filter(src => /\.[a-z0-9]{8,}\.js$/i.test(src))
            .sort();

        if (scripts.length) {
            return scripts.join('|');
        }

        return this.hashText(indexHtml);
    }

    private captureCurrentVersion(): void {
        this.fetchIndexHtml()
            .then(indexHtml => this.currentSignature = this.getBundleSignature(indexHtml))
            .catch(() => undefined);
    }

    private compareCurrentVersion(): void {
        this.checkInProgress = true;
        this.fetchIndexHtml()
            .then(indexHtml => {
                const latestSignature = this.getBundleSignature(indexHtml);

                if (!this.currentSignature) {
                    this.currentSignature = latestSignature;
                    return;
                }

                console.log(`Current signature: ${this.currentSignature}`);
                console.log(`Latest signature: ${latestSignature}`);

                if (latestSignature !== this.currentSignature) {
                    this.updateAvailable.next(true);
                }
            })
            .catch(() => undefined)
            .finally(() => this.checkInProgress = false);
    }

    private fetchIndexHtml(): Promise<string> {
        const url = new URL('index.html', window.location.origin);
        url.searchParams.set('_', Date.now().toString());

        return fetch(url.toString(), {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
            },
        }).then(response => {
            if (!response.ok) {
                throw new Error(`Version check failed with status ${response.status}`);
            }

            return response.text();
        });
    }

    private getScriptSources(indexHtml: string): string[] {
        const sources: string[] = [];
        const scriptRegex = /<script[^>]+src=["']([^"']+\.js(?:\?[^"']*)?)["'][^>]*>/gi;
        let match = scriptRegex.exec(indexHtml);

        while (match) {
            sources.push(match[1]);
            match = scriptRegex.exec(indexHtml);
        }

        return sources;
    }

    private normalizeScriptSource(src: string): string {
        const url = new URL(src, window.location.origin);
        const segments = url.pathname.split('/');
        return segments[segments.length - 1];
    }

    private hashText(text: string): string {
        let hash = 0;

        for (let index = 0; index < text.length; index++) {
            hash = ((hash << 5) - hash) + text.charCodeAt(index);
            hash |= 0;
        }

        return hash.toString();
    }
}
